import React from "react";
import { ClassNameProps, Column, Grid, makeClassName, Row, StyleProps } from "@hex-insights/core";
import { AttendanceRecordCountStatistics } from "../../../../../../../Utilities";
import { AttendanceStatusUtilities } from "../../../../../../Utilities";
import styles from "./styles.module.css";

export type SchoolAttendanceRecordDonutChartDatum = {
	value: number;
	color: string;
	label: string;
};

export type SchoolAttendanceRecordDonutChartProps = {
	data: SchoolAttendanceRecordDonutChartDatum[];
	centerValue: number;
	centerLabel: string;
	size?: string;
} & Partial<ClassNameProps & StyleProps>;

export function SchoolAttendanceRecordDonutChart({
	data,
	centerValue,
	centerLabel,
	size,
	className,
	style,
}: SchoolAttendanceRecordDonutChartProps) {
	const background = React.useMemo(() => {
		const totalValue = data.reduce((acc, e) => acc + e.value, 0);
		if (totalValue === 0) {
			return AttendanceStatusUtilities.backgroundColor(null);
		}
		const slices = [];
		let cumulativeValue = 0;
		for (let i = 0; i < data.length; i++) {
			const datum = data[i];
			cumulativeValue += datum.value;
			slices.push(`${datum.color} 0 ${(cumulativeValue / totalValue) * 100}%`);
		}
		return `conic-gradient(${slices.join(", ")})`;
	}, [data]);

	return (
		<div
			className={makeClassName(styles["donut-chart"], className)}
			style={{ background, "--size": size, ...style } as React.CSSProperties}
		>
			<Column
				justify="spaced-start"
				align="center"
				verticalSpacing="0.375rem"
				className={makeClassName(styles["donut-chart__center-content"])}
			>
				<span className={makeClassName(styles["donut-chart__center-content__value"])}>{centerValue}</span>
				<span className={makeClassName(styles["donut-chart__center-content__label"])}>{centerLabel}</span>
			</Column>
		</div>
	);
}

export type SchoolAttendanceRecordDonutChartLegendProps = {
	data: SchoolAttendanceRecordDonutChartDatum[];
} & Partial<ClassNameProps & StyleProps>;

export function SchoolAttendanceRecordDonutChartLegend({
	data,
	className,
	style,
}: SchoolAttendanceRecordDonutChartLegendProps) {
	return (
		<Column
			justify="spaced-start"
			verticalSpacing="0.5rem"
			className={makeClassName(styles["donut-chart-legend"], className)}
			style={style}
		>
			{data.map((datum) => (
				<Row key={datum.label} justify="spaced-start" align="center" horizontalSpacing="0.5rem">
					<div className={styles["donut-chart-legend__dot"]} style={{ backgroundColor: datum.color }}></div>
					<span className={styles["donut-chart-legend__label"]}>{datum.label}</span>
					<span className={styles["donut-chart-legend__value"]}>{datum.value}</span>
				</Row>
			))}
		</Column>
	);
}

export type SchoolAttendanceRecordDonutChartGridLegendProps = {
	data: SchoolAttendanceRecordDonutChartDatum[];
} & Partial<ClassNameProps & StyleProps>;

export function SchoolAttendanceRecordDonutChartGridLegend({
	data,
	className,
	style,
}: SchoolAttendanceRecordDonutChartGridLegendProps) {
	return (
		<Grid tracks={2} gap="0.75rem" className={makeClassName(styles["donut-chart-legend"], className)} style={style}>
			{data.map((datum) => (
				<Column
					key={datum.label}
					justify="spaced-start"
					align="center"
					verticalSpacing="0.5rem"
					className={styles["donut-chart-grid-legend__item"]}
				>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<div className={styles["donut-chart-legend__dot"]} style={{ backgroundColor: datum.color }}></div>
						<span className={styles["donut-chart-legend__label"]}>{datum.label}</span>
					</Row>

					<span className={styles["donut-chart-legend__value"]}>{datum.value}</span>
				</Column>
			))}
		</Grid>
	);
}

const defaultStatus = {
	indicatesAbsent: false,
	indicatesLateArrival: false,
	indicatesEarlyDeparture: false,
	indicatesExcused: false,
};

export function statisticsToDonutChartData(
	statistics:
		| Pick<
				AttendanceRecordCountStatistics,
				"present" | "absentTotal" | "lateArrivalTotal" | "earlyDepartureTotal" | "notTaken"
		  >
		| null
		| undefined,
) {
	if (!statistics) {
		return [];
	}

	return [
		{
			color: AttendanceStatusUtilities.color({ ...defaultStatus }),
			value: statistics.present,
			label: "Present",
		},
		{
			color: AttendanceStatusUtilities.color({ ...defaultStatus, indicatesAbsent: true }),
			value: statistics.absentTotal,
			label: "Absent",
		},
		{
			color: AttendanceStatusUtilities.color({ ...defaultStatus, indicatesLateArrival: true }),
			value: statistics.lateArrivalTotal + statistics.earlyDepartureTotal,
			label: "Tardy",
		},
		{
			color: AttendanceStatusUtilities.backgroundColor(null),
			value: statistics.notTaken,
			label: "Not Taken",
		},
	];
}
